import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface FilterColumnMapping {
  ColumnName: string;
  ObjectType: string;
  ObjectName: string;
}

export interface PublicAudienceVolume {
  ColumnName: string;
  ObjectType: string;
  ObjectName: string;
  UpdateDate: string;
  Volumes: number;
  PopulationShare: number;
}

export interface ChartTotalsRequest {
  DaysBack: number;
  FilterColumns?: string[];
}

export interface ChartDailyRequest {
  DaysBack: number;
  FilterColumns?: string[];
  ResultColumns?: string[];
}

export interface GenericResponse<T> {
  payload?: T;
  error?: {
    status: number;
    message: string;
  };
}

export interface TableRow {
  audience_id?: string;
  audience_name?: string;
  personas_id?: string;
  personas_name?: string;
  device_id?: string;
  device_name?: string;
  source_id?: string;
  source_name?: string;
  goal_id?: string;
  goal_name?: string;
  user_count?: number;
  [key: string]: any;
}

export interface TableDataRequest {
  DaysBack: number;
  ObjectTypes?: string[];
  FilterColumns?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AudienceDashboardService {
  private apiUrl = this.resolveBaseUrl(environment.apiUrl);
  private activeDcrId: string | null = null;
  private currentCacheKey: string | null = null;

  tableDataList: Record<string, BehaviorSubject<TableRow[] | null> | undefined> = {};
  chartDailyTrendList: Record<string, BehaviorSubject<GenericResponse<any[]> | null> | undefined> = {};
  chartTotalsList: Record<string, BehaviorSubject<GenericResponse<any> | null> | undefined> = {};
  publicAudienceVolumes: BehaviorSubject<GenericResponse<PublicAudienceVolume[]> | null> = new BehaviorSubject<GenericResponse<PublicAudienceVolume[]> | null>(null);

  constructor(private http: HttpClient, private authService: AuthService) {
    this.activeDcrId = this.authService.getActiveDcr();

    this.authService.currentUser$.subscribe(user => {
      const newDcrId = user?.entityID ?? null;
      const newKey = user ? `${user.ID}:${newDcrId ?? ''}` : null;

      if (this.currentCacheKey !== newKey) {
        this.currentCacheKey = newKey;
        this.activeDcrId = newDcrId;
        this.resetCaches();
      }
    });

    this.authService.selectedDcr$
      .pipe(distinctUntilChanged())
      .subscribe(dcrId => {
        if (this.activeDcrId === dcrId) {
          return;
        }
        this.activeDcrId = dcrId;
        this.resetCaches();
      });
  }

  private resolveBaseUrl(url: string): string {
    if (!url) {
      return '';
    }
    return url.replace(/\/api$/u, '');
  }

  private resetCaches(): void {
    this.tableDataList = {};
    this.chartDailyTrendList = {};
    this.chartTotalsList = {};
    this.publicAudienceVolumes.next(null);
  }

  /**
   * Genera un hash SHA-256 per una stringa usando Web Crypto API nativa del browser
   */
  private async generateHash(text: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  }

  private waitForEntitySync(): Observable<void> {
    return this.authService.entitySyncPending$.pipe(
      filter(pending => !pending),
      take(1),
      map(() => undefined)
    );
  }

  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders(this.authService.getAuthHeaders());

    if (!headers.has('Content-Type')) {
      headers = headers.set('Content-Type', 'application/json');
    }

    headers = headers.set('X-Product', 'vision');

    return headers;
  }

  /**
   * Ottiene il mapping delle colonne disponibili per i filtri
   */
  getFilters(): Observable<GenericResponse<FilterColumnMapping[]>> {
    return this.waitForEntitySync().pipe(
      switchMap(() => this.http.post<GenericResponse<FilterColumnMapping[]>>(
        `${this.apiUrl}/webapp/audience-engine/get-filters`,
        {},
        { headers: this.getHeaders() }
      ))
    );
  }

  /**
   * Ottiene i volumi delle audience pubbliche con quota popolazione
   */
  getPublicAudienceVolumes(): Observable<GenericResponse<PublicAudienceVolume[]>> {
    return new Observable(observer => {
      // Se non è ancora stato caricato, avvia il caricamento
      if (this.publicAudienceVolumes.value === null) {
        this.loadPublicAudienceVolumes();
      }

      // Filtra i valori nulli e prende il primo valore valido, poi completa
      this.publicAudienceVolumes.asObservable().pipe(
        filter(data => data !== null && data !== undefined),
        take(1)
      ).subscribe({
        next: (data: any) => {
          observer.next(data);
          observer.complete();
        },
        error: (err: any) => observer.error(err)
      });
    });
  }

  private loadPublicAudienceVolumes(): void {
    this.waitForEntitySync().pipe(
      switchMap(() => this.http.post<GenericResponse<PublicAudienceVolume[]>>(
        `${this.apiUrl}/webapp/audience-engine/get-public-audience-volumes`,
        {},
        { headers: this.getHeaders() }
      ))
    ).subscribe({
      next: (response) => {
        this.publicAudienceVolumes.next(response);
      },
      error: (error) => {
        console.error('Errore nel caricamento dei volumi delle audience pubbliche:', error);
        this.publicAudienceVolumes.next({
          error: {
            status: error.status || 500,
            message: error.message || 'Errore durante il caricamento dei volumi'
          }
        });
      }
    });
  }

  /**
   * Ottiene i totali aggregati
   * @param daysBack - Numero di giorni da considerare
   * @param filterColumns - Colonne opzionali da filtrare
   */
  getChartTotals(daysBack: number, filterColumns?: string[]): Observable<GenericResponse<any>> {
    return new Observable(observer => {
      const body: ChartTotalsRequest = {
        DaysBack: daysBack,
        FilterColumns: filterColumns
      };

      this.generateHash(JSON.stringify(body)).then(key => {
        let currentLoaded = this.chartTotalsList[key];

        if (!currentLoaded) {
          currentLoaded = new BehaviorSubject<GenericResponse<any> | null>(null);
          this.chartTotalsList[key] = currentLoaded;
          this.loadChartTotals(body, currentLoaded);
        }

        // Filtra i valori nulli e prende il primo valore valido, poi completa
        currentLoaded.asObservable().pipe(
          filter(data => data !== null && data !== undefined),
          take(1)
        ).subscribe({
          next: (data: any) => {
            observer.next(data);
            observer.complete();
          },
          error: (err: any) => observer.error(err)
        });
      });
    });
  }

  private loadChartTotals(body: ChartTotalsRequest, list: BehaviorSubject<GenericResponse<any> | null>): void {
    this.waitForEntitySync().pipe(
      switchMap(() => this.http.post<GenericResponse<any>>(
        `${this.apiUrl}/webapp/audience-engine/get-chart-totals`,
        body,
        { headers: this.getHeaders() }
      ))
    ).subscribe({
      next: (res: GenericResponse<any>) => {
        list.next(res);
      },
      error: err => {
        list.next({ error: { status: 500, message: err.message || 'Unknown error' } });
      }
    });
  }

  /**
   * Ottiene il trend giornaliero
   * @param daysBack - Numero di giorni da considerare
   * @param filterColumns - Colonne opzionali da filtrare
   * @param resultColumns - Colonne opzionali da includere nei risultati
   */
  getChartDailyTrend(
    daysBack: number,
    filterColumns?: string[],
    resultColumns?: string[]
  ): Observable<GenericResponse<any[]>> {
    return new Observable(observer => {
      const body: ChartDailyRequest = {
        DaysBack: daysBack,
        FilterColumns: filterColumns,
        ResultColumns: resultColumns
      };

      this.generateHash(JSON.stringify(body)).then(key => {
        let currentLoaded = this.chartDailyTrendList[key];

        if (!currentLoaded) {
          currentLoaded = new BehaviorSubject<GenericResponse<any[]> | null>(null);
          this.chartDailyTrendList[key] = currentLoaded;
          this.loadChartDailyTrend(body, currentLoaded);
        }

        // Filtra i valori nulli e prende il primo valore valido, poi completa
        currentLoaded.asObservable().pipe(
          filter(data => data !== null && data !== undefined),
          take(1)
        ).subscribe({
          next: (data: any) => {
            observer.next(data);
            observer.complete();
          },
          error: (err: any) => observer.error(err)
        });
      });
    });
  }

  private loadChartDailyTrend(body: ChartDailyRequest, list: BehaviorSubject<GenericResponse<any[]> | null>): void {
    this.waitForEntitySync().pipe(
      switchMap(() => this.http.post<GenericResponse<any[]>>(
        `${this.apiUrl}/webapp/audience-engine/get-chart-daily-trend`,
        body,
        { headers: this.getHeaders() }
      ))
    ).subscribe({
      next: (res: GenericResponse<any[]>) => {
        list.next(res);

        // OPTIMIZATION: Se questa richiesta ha ResultColumns popolate,
        // cachea anche la versione con ResultColumns vuote (perché TNCIDs è sempre presente)
        if (body.ResultColumns && body.ResultColumns.length > 0) {
          const baseBody: ChartDailyRequest = {
            DaysBack: body.DaysBack,
            FilterColumns: body.FilterColumns,
            ResultColumns: []
          };

          this.generateHash(JSON.stringify(baseBody)).then(baseKey => {
            // Se non esiste già la cache per la versione base, creala
            if (!this.chartDailyTrendList[baseKey]) {
              this.chartDailyTrendList[baseKey] = new BehaviorSubject<GenericResponse<any[]> | null>(res);
            }
          });
        }
      },
      error: err => {
        list.next({ error: { status: 500, message: err.message || 'Unknown error' } });
      }
    });
  }

  /**
   * Ottiene i dati della tabella con tutte le combinazioni
   * @param daysBack - Numero di giorni da considerare
   * @param visibleColumns - Colonne visibili nella tabella
   */
  getTableData(daysBack: number, visibleColumns?: ('audience' | 'device' | 'enriched' | 'goal' | 'personas' | 'source')[], filterColumns?: string[]): Observable<TableRow[]> {
    return new Observable(observer => {
      const body: TableDataRequest = {
        DaysBack: daysBack,
        ObjectTypes: visibleColumns,
        FilterColumns: filterColumns || []
      };

      this.generateHash(JSON.stringify(body)).then(key => {
        let currentLoaded = this.tableDataList[key];

        if (!currentLoaded) {
          currentLoaded = new BehaviorSubject<TableRow[] | null>(null);
          this.tableDataList[key] = currentLoaded;
          this.loadTableData(body, currentLoaded);
        }

        // Filtra i valori nulli e prende il primo valore valido, poi completa
        currentLoaded.asObservable().pipe(
          filter(data => data !== null && data !== undefined),
          take(1)
        ).subscribe({
          next: (data: any) => {
            observer.next(data);
            observer.complete();
          },
          error: (err: any) => observer.error(err)
        });
      });
    });
  }

  private loadTableData(body: TableDataRequest, list: BehaviorSubject<TableRow[] | null>): void {
    this.waitForEntitySync().pipe(
      switchMap(() => this.http.post<GenericResponse<TableRow[]>>(
        `${this.apiUrl}/webapp/audience-engine/get-table-data`,
        body,
        { headers: this.getHeaders() }
      ))
    ).subscribe({
      next: (res: GenericResponse<TableRow[]>) => {
        const data = res.payload as TableRow[] || [];
        list.next(data);
      },
      error: err => {
        list.next([]);
      }
    });
  }
}
